import {defineNuxtPlugin, useHead} from '#app';
import { type ExportedGlobalComposer} from 'vue-i18n';

interface NuxtI18nCustom {
    locale: string;
    setLocale: (locale: string) => Promise<void> | void;
    getLocaleCookie: () => string | undefined;
    getBrowserLocale?: () => string | undefined;
}

interface NuxtAppCustom {
    $i18n: ExportedGlobalComposer & NuxtI18nCustom;
    $changeLocale: (locale: string) => void;
}

const SUPPORTED_LOCALES = ['en', 'es', 'vi', 'ko', 'ja'] as const;

export default defineNuxtPlugin((nuxtApp) => {
    const $i18n = nuxtApp.$i18n as NuxtAppCustom['$i18n'];

    const cookieLocale = $i18n.getLocaleCookie?.();
    const browserLocale = process.client ? $i18n.getBrowserLocale?.() : undefined;
    const candidate = cookieLocale || browserLocale || 'en';
    const initialLocale: string = (SUPPORTED_LOCALES as readonly string[]).includes(candidate)
        ? candidate
        : 'en';

    const reactiveLocale = ref($i18n.locale);
    // setLocale is called but not awaited -- @nuxtjs/i18n's own
    // route-locale-detect plugin (which runs before us via `dependsOn: i18n:plugin`)
    // has already set the locale to the detected value during its setup, so this
    // call is typically a no-op. We still fire it to cover any edge case where
    // the route detection diverges from cookie/browser. The reveal-on-render
    // gating below (paired with the inline anti-FOUC hide) prevents the en flash.
    $i18n.setLocale(initialLocale);
    reactiveLocale.value = initialLocale;

    useHead({
        htmlAttrs: {
            lang: initialLocale
        }
    });

    if (process.client) {
        // The inline anti-FOUC script in nuxt.config.ts hides <html>
        // (visibility:hidden) before first paint whenever the resolved locale is
        // non-en, so the prerendered English text never flashes before vue-i18n
        // re-renders into the target locale. We reveal again as soon as the first
        // client render has committed: by `app:mounted` the i18n plugin has already
        // applied the locale synchronously, and a `nextTick` + double rAF guarantees
        // the locale-patched DOM is laid out and about to paint.
        //
        // IMPORTANT: we deliberately do NOT gate the reveal on inspecting body text.
        // The previous implementation polled `body.innerText` for the absence of
        // English UI strings, but innerText is empty/unreliable while <html> is
        // visibility:hidden and the heuristic frequently never matched -- so the
        // reveal fell through to a multi-second timeout, leaving users on a blank
        // white page on every load/refresh (and the onboarding SDK overlay, which
        // renders outside the hidden tree, showing alone). Revealing on the
        // render-commit signal makes the worst realistic case a sub-frame en flash
        // instead of a 3-4s white screen; the inline script's short safety timeout
        // is the only remaining backstop if JS never reaches this point.
        const revealDocument = () => {
            const safety = (window as any).__i18nSafety;
            if (safety) {
                clearTimeout(safety);
                (window as any).__i18nSafety = null;
            }
            const el = document.documentElement;
            el.style.visibility = '';
            el.removeAttribute('data-i18n-pending');
        };

        // En visitors are never hidden by the inline script, so this is a harmless
        // no-op for them; non-en visitors get un-hidden on the first painted frame.
        nuxtApp.hook('app:mounted', async () => {
            await nextTick();
            requestAnimationFrame(() => requestAnimationFrame(revealDocument));
        });

        watch(reactiveLocale, (newLocale) => {
            useHead({
                htmlAttrs: {
                    lang: newLocale
                }
            });
        });
    }

    return {
        provide: {
            trans: (id: string) => ($i18n as any).t(id)
        }
    };
});
